/* eslint-disable react/require-default-props */
import * as React from 'react';
import {
  Platform,
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
  TextStyle,
  ViewStyle,
} from 'react-native';
import {clamp, pick} from 'lodash/fp';

import testProperties from '/util/testing/testProperties';

import {
  disabledTextColor,
  lineHeight,
  placeholderColor,
  styles,
} from './TextInput.style';

// This is not all the props the built-in TextInput component could accept. This is only a subset
// of those props because it would be too long to list all of them and many of them aren't
// necessarily useful for us anyway. If there is ever a need, we can always add to this list.
export const TEXT_INPUT_PROPS = [
  'autoCapitalize',
  'autoCompleteType',
  'autoCorrect',
  'autoFocus',
  'blurOnSubmit',
  'clearTextOnFocus',
  'defaultValue',
  'keyboardAppearance',
  'keyboardType',
  'maxLength',
  'onBlur',
  'onChange',
  'onFocus',
  'onChangeText',
  'placeholder',
  'placeholderTextColor',
  'returnKeyLabel',
  'returnKeyType',
  'secureTextEntry',
  'spellCheck',
  'textAlign',
  'textContentType',
  'value',
  'accessible',
  'accessibilityLabel',
  'accessibilityHint',
  'accessibilityRole',
  'accessibilityState',
  'accessibilityLiveRegion',
  'importantForAccessibility',
] as const;

export const pickTextInputProps = pick(TEXT_INPUT_PROPS);

interface TextInputProps extends RNTextInputProps {
  style?: ViewStyle;
  disabled?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  innerRef?: React.Ref<RNTextInput>;
  testID?: string | undefined;
  webID?: string;
  greyOutTextInputValue?: boolean;
  selectionColor?: string | undefined;
  showSoftInputOnFocus?: boolean;
}

export const TextInput: React.FC<TextInputProps> = ({
  style: styleProp,
  disabled = false,
  multiline = false,
  numberOfLines = 1,
  innerRef = null,
  testID,
  webID = undefined,
  greyOutTextInputValue = false,
  selectionColor,
  showSoftInputOnFocus = true,
  ...restProps
}) => {
  const linesCount = multiline ? clamp(1, Infinity)(numberOfLines) : 1;

  const style: ViewStyle[] = [styles.base];

  if (disabled && greyOutTextInputValue) {
    style.push({color: disabledTextColor} as TextStyle);
  }

  if (linesCount > 1) {
    style.push({minHeight: linesCount * lineHeight});
  }

  if (styleProp) {
    style.push(styleProp);
  }

  return (
    <RNTextInput
      ref={innerRef}
      placeholderTextColor={disabled ? disabledTextColor : placeholderColor}
      {...pickTextInputProps(restProps)}
      style={style}
      editable={!disabled}
      multiline={multiline}
      numberOfLines={linesCount}
      selectionColor={selectionColor}
      showSoftInputOnFocus={showSoftInputOnFocus}
      textAlignVertical={linesCount > 1 ? 'top' : undefined}
      nativeID={Platform.OS === 'web' ? webID : undefined}
      id={Platform.OS === 'web' ? webID : undefined}
      {...testProperties(testID ?? null)}
    />
  );
};

export default React.forwardRef<RNTextInput, TextInputProps>(
  (props, innerRef) => <TextInput {...props} innerRef={innerRef} />,
);
